import { useState, useCallback, useRef, useEffect } from "react";
import { createOrder, getStatus } from "../../services/flightBookingAPI";

// Booking status constants
export const BookingStatus = {
  PAYMENT_PENDING: "PAYMENT_PENDING",
  PAYMENT_SUCCESS: "PAYMENT_SUCCESS",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  BOOKING_IN_PROGRESS: "BOOKING_IN_PROGRESS",
  TICKET_ISSUED: "TICKET_ISSUED",
  TICKET_FAILED: "TICKET_FAILED",
};

// Polling configuration
const POLLING_INTERVAL = 3000; // 3 seconds
const MAX_POLLING_ATTEMPTS = 100; // 5 minutes max (100 * 3 seconds)

export const usePaymentFlow = () => {
  const [paymentState, setPaymentState] = useState({
    isProcessing: false,
    status: null,
    error: null,
    orderData: null,
    statusData: null,
  });

  const pollingIntervalRef = useRef(null);
  const pollingAttemptsRef = useRef(0);

  // Clear polling interval
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  // Start status polling
  const startStatusPolling = useCallback(
    async (rc_booking_id, trace_id, result_idx, amount, userToken) => {
      clearPolling();

      const pollStatus = async () => {
        try {
          pollingAttemptsRef.current += 1;

          // console.log(`[Polling Attempt ${pollingAttemptsRef.current}/${MAX_POLLING_ATTEMPTS}]`);

          const response = await getStatus(
            rc_booking_id,
            trace_id,
            result_idx,
            amount,
            userToken
          );

          // console.log("[Poll Response]", response);

          const currentStatus = response.data?.status;

          // Update state with latest status data
          setPaymentState((prev) => ({
            ...prev,
            status: currentStatus,
            statusData: response.data,
          }));

          // Check if we should stop polling
          const terminalStatuses = [
            BookingStatus.PAYMENT_FAILED,
            BookingStatus.TICKET_ISSUED,
            BookingStatus.TICKET_FAILED,
          ];

          if (terminalStatuses.includes(currentStatus)) {
            //console.log(`[Polling Complete] Terminal status reached: ${currentStatus}`);
            clearPolling();
            setPaymentState((prev) => ({
              ...prev,
              isProcessing: false,
            }));
          } else if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
            //console.log("[Polling Timeout] Max attempts reached");
            clearPolling();
            setPaymentState((prev) => ({
              ...prev,
              isProcessing: false,
              status: BookingStatus.TICKET_FAILED,
              error: "Booking timeout. Please contact support.",
            }));
          }
        } catch (error) {
          console.error("[Polling Error]", error);

          // Continue polling on network errors, stop on other errors
          if (error.message.includes("network") || error.message.includes("timeout")) {
            //console.log("[Polling] Network error, continuing...");
          } else {
            clearPolling();
            setPaymentState((prev) => ({
              ...prev,
              isProcessing: false,
              status: BookingStatus.TICKET_FAILED,
              error: error.message || "An error occurred while checking status",
            }));
          }
        }
      };

      // Initial poll
      await pollStatus();

      // Set up interval polling
      pollingIntervalRef.current = setInterval(pollStatus, POLLING_INTERVAL);
    },
    [clearPolling]
  );

  // Initiate payment flow
  const initiatePayment = useCallback(
    async (rc_booking_id, trace_id, result_idx, amount, userToken) => {
      try {
        setPaymentState({
          isProcessing: true,
          status: "CREATING_ORDER",
          error: null,
          orderData: null,
          statusData: null,
        });

        // console.log("[Initiating Payment]", {
        //   rc_booking_id,
        //   trace_id,
        //   result_idx,
        //   amount,
        // });

        // Step 1: Create order
        const orderResponse = await createOrder(
          rc_booking_id,
          trace_id,
          result_idx,
          amount,
          userToken
        );

        // console.log("[Order Created]", orderResponse);

        setPaymentState((prev) => ({
          ...prev,
          orderData: orderResponse.data,
          status: orderResponse.data?.status,
        }));

        // Step 2: Start polling for status
        await startStatusPolling(
          rc_booking_id,
          trace_id,
          result_idx,
          amount,
          userToken
        );
      } catch (error) {
        console.error("[Payment Initiation Error]", error);
        setPaymentState({
          isProcessing: false,
          status: BookingStatus.PAYMENT_FAILED,
          error: error.message || "Failed to create order",
          orderData: null,
          statusData: null,
        });
      }
    },
    [startStatusPolling]
  );

  // Reset payment state
  const resetPayment = useCallback(() => {
    clearPolling();
    setPaymentState({
      isProcessing: false,
      status: null,
      error: null,
      orderData: null,
      statusData: null,
    });
  }, [clearPolling]);

  return {
    paymentState,
    initiatePayment,
    resetPayment,
  };
};
